/**
 * Configuration file for Yachangu mobile and web app
 * Handles platform-specific configurations including API keys
 */

import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform(); // 'web', 'ios', 'android'

/**
 * Google Maps API Key Configuration
 * 
 * For security in production:
 * 1. Web: Use environment variables (REACT_APP_GOOGLE_MAPS_API_KEY)
 * 2. Native: Keys are embedded during build, restricted by bundle ID
 * 
 * Current Setup:
 * - The same key works for web, iOS, and Android
 * - Key restrictions should be set in Google Cloud Console:
 *   - Web: HTTP referrers (*.emergentagent.com)
 *   - iOS: Bundle ID (com.yachangu.app)
 *   - Android: Package name + SHA-1 fingerprint
 */
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY_NATIVE;

/**
 * Backend API URL
 * Automatically uses the correct URL for web vs native
 */
export const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * App Configuration
 */
export const APP_CONFIG = {
  appId: 'com.yachangu.app',
  appName: 'Yachangu',
  version: '1.0.0',
  
  // Platform info
  platform,
  isNative,
  isWeb: platform === 'web',
  isIOS: platform === 'ios',
  isAndroid: platform === 'android',
  
  // Feature flags
  features: {
    enablePushNotifications: isNative,
    enableHapticFeedback: isNative,
    enableBackgroundTracking: isNative,
    enableOfflineMode: isNative
  },
  
  // Map configuration
  map: {
    defaultCenter: { lat: -13.9833, lng: 33.7833 }, // Lilongwe, Malawi
    defaultZoom: 14,
    libraries: ['places', 'geometry', 'drawing']
  },
  
  // Timeouts
  timeouts: {
    driverSearch: 120, // 2 minutes
    rideExpiry: 3600, // 1 hour
    pollingInterval: 5000 // 5 seconds
  }
};

/**
 * Validate configuration on load
 */
export const validateConfig = () => {
  const errors = [];
  
  if (!GOOGLE_MAPS_API_KEY) {
    errors.push('Google Maps API key is missing');
  }
  
  if (!BACKEND_API_URL) {
    errors.push('Backend API URL is missing');
  }
  
  if (errors.length > 0) {
    console.error('❌ Configuration Errors:', errors);
    return { valid: false, errors };
  }
  
  console.log('✅ Configuration validated successfully', {
    platform,
    isNative,
    hasGoogleMapsKey: !!GOOGLE_MAPS_API_KEY,
    backendUrl: BACKEND_API_URL
  });
  
  return { valid: true, errors: [] };
};

// Auto-validate on import
validateConfig();

export default {
  GOOGLE_MAPS_API_KEY,
  BACKEND_API_URL,
  APP_CONFIG,
  validateConfig
};
